import React, { useCallback, useRef } from 'react';

import Joi from '@hapi/joi';
import { Formik } from 'formik';
import { toast } from 'react-toastify';

import PasswordResetView from '../PasswordResetView/PasswordResetView';

import { useMutation, gql } from '@apollo/client';
import { logger } from 'RootShared/utility/ErrorLogger';

const mutationResetPassword = gql`
  mutation PasswordResetFormMutation($email: String!) @api(name: root) {
    resetPassword(email: $email) {
      result
    }
  }
`;

const resetPasswordValidationSchema = Joi.object().keys({
  email: Joi.string()
    .email()
    .error(() => 'Entered email is not valid'),
});

const PasswordResetForm = () => {
  const [resetPassword] = useMutation(mutationResetPassword, {
    onCompleted(data) {
      onResetPasswordSuccess(data);
    },
    onError(error) {
      onPasswordResetError(error);
    },
  });

  const initialValues = {
    email: '',
  };

  const toastId = useRef(null);

  const showNotification = useCallback(
    () =>
      (toastId.current = toast('In progress, please wait...', {
        autoClose: true,
        closeButton: false,
        hideProgressBar: true,
      })),
    []
  );

  const onResetPasswordSuccess = useCallback(
    (data) => {
      if (!data.errors && data.resetPassword.result) {
        toast.update(toastId.current, {
          render: 'Instructions has been sent to your email',
          type: toast.TYPE.SUCCESS,
          autoClose: 3000,
          closeButton: true,
          hideProgressBar: false,
        });
      } else {
        toast.update(toastId.current, {
          render: 'Email sending error',
          type: toast.TYPE.ERROR,
          autoClose: 3000,
          closeButton: true,
          hideProgressBar: false,
        });
      }
    },
    [toastId]
  );

  const onPasswordResetError = useCallback(
    (error) => {
      logger.error(error);
      toast.update(toastId.current, {
        render: error.message || 'Email sending error',
        type: toast.TYPE.ERROR,
        autoClose: 3000,
        closeButton: true,
        hideProgressBar: false,
      });
      return error;
    },
    [toastId]
  );

  const handleResetPasswordSubmit = useCallback(
    (values, { setSubmitting, setValues }) => {
      showNotification();

      resetPassword({ variables: values });

      setSubmitting(false);
      setValues(initialValues);
    },
    [resetPassword, initialValues, showNotification]
  );

  const handleResetPasswordValidation = useCallback(async (values) => {
    const validationResult = await Joi.validate(
      {
        ...values,
      },
      resetPasswordValidationSchema,
      {
        abortEarly: false,
      }
    );

    return validationResult.error;
  }, []);

  const renderFunction = useCallback((formikProps) => <PasswordResetView formikProps={formikProps} />, []);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleResetPasswordSubmit}
      validate={handleResetPasswordValidation}
      validateOnChange={false}
      validateOnBlur={true}
      render={renderFunction}
    />
  );
};

export default PasswordResetForm;
